import { useState, useEffect, useCallback } from 'react';
import { extractToc } from '../utils/blogUtils';
import type { ArticlePost } from '@/lib/articles';

interface Props {
  post: ArticlePost;
}

const TableOfContents = ({ post }: Props) => {
  const [activeId, setActiveId] = useState<string>('');
  const [readPct, setReadPct] = useState(0);
  const [collapsed, setCollapsed] = useState(false);

  const items = extractToc(post.content);

  /* Track reading progress */
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setReadPct(h > 0 ? Math.round(Math.min(100, (window.scrollY / h) * 100)) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Active heading via IntersectionObserver */
  useEffect(() => {
    if (items.length === 0) return;

    const els = items
      .map((item) => document.getElementById(item.id))
      .filter(Boolean) as HTMLElement[];

    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-72px 0px -55% 0px', threshold: 0 }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 88;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-list-check-2 text-violet-500 text-base" />
          </div>
          <span className="text-sm font-bold text-[#0A1628]">文章目錄</span>
          <span className="text-[11px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded-full">
            {items.filter((i) => i.level === 2).length} 節
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-violet-600 font-semibold">{readPct}%</span>
          <i className={`ri-arrow-${collapsed ? 'down' : 'up'}-s-line text-slate-400 text-base`} />
        </div>
      </button>

      {/* Mini progress bar */}
      <div className="h-1 bg-slate-100 mx-5 rounded-full overflow-hidden -mt-1 mb-0">
        <div
          className="h-full bg-gradient-to-r from-violet-500 to-violet-600 rounded-full transition-[width] duration-300"
          style={{ width: `${readPct}%` }}
        />
      </div>

      {/* Nav items */}
      {!collapsed && (
        <nav className="px-3 pt-3 pb-4 space-y-0.5 max-h-72 overflow-y-auto">
          {items.map((item, idx) => {
            const isActive = activeId === item.id;
            return (
              <button
                key={`${item.id}-${idx}`}
                onClick={() => scrollTo(item.id)}
                className={`w-full text-left flex items-start gap-2 px-3 py-2 rounded-lg text-xs transition-all cursor-pointer whitespace-normal group ${
                  item.level === 3 ? 'pl-7' : ''
                } ${
                  isActive
                    ? 'bg-violet-50 text-violet-700 font-semibold'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
              >
                {/* dot indicator */}
                <span
                  className={`flex-shrink-0 rounded-full transition-all ${
                    item.level === 2
                      ? `w-1.5 h-1.5 mt-1.5 ${isActive ? 'bg-violet-500' : 'bg-slate-300 group-hover:bg-slate-400'}`
                      : `w-1 h-1 mt-2 ${isActive ? 'bg-violet-400' : 'bg-slate-200 group-hover:bg-slate-300'}`
                  }`}
                />
                <span className="leading-relaxed">{item.text}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* Footer: estimated reading time left */}
      {!collapsed && (
        <div className="px-5 pb-4 flex items-center gap-1.5">
          <i className="ri-time-line text-slate-400 text-xs" />
          <span className="text-[11px] text-slate-400">
            約剩 {Math.max(0, Math.round(post.readTime * (1 - readPct / 100)))} 分鐘讀完
          </span>
        </div>
      )}
    </div>
  );
};

export default TableOfContents;
